import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Button, Typography } from '@mui/material';

import { useCopyToClipboard } from 'utils/hooks';
import { colors } from 'utils/constants';
import { Popover } from 'common/components';

const ImageWrapper = styled.figure`
  display: flex;
  justify-content: center;
  margin: 0 auto;
  border: 1px solid;
  border-color: ${({ $draftStatus }) =>
    $draftStatus ? colors.warning : colors.borderTransparentGray};
  cursor: pointer;

  &:hover {
    background-color: ${colors.borderTransparentGray};
  }
`;

const WidgetImage = styled.img`
  height: 300px;
`;

const WidgetHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

/**
 * Preview card for an embeddable widget: a clickable preview image, a
 * title/subtitle header, and a "Copy Code" button that copies the embed
 * snippet to the clipboard. When the parent entity is a draft, the image
 * border is highlighted and a warning popover is shown. Used on the action
 * center Widgets tab.
 */
const WidgetCard = ({
  draftStatus,
  draftMessage,
  imageSrc,
  imageAlt,
  title,
  subtitle,
  widgetCode,
  onImageClick,
}) => {
  const [copy] = useCopyToClipboard();

  return (
    <Popover shouldDisplayPopover={draftStatus} text={draftMessage}>
      <div>
        <ImageWrapper $draftStatus={draftStatus} onClick={() => onImageClick(widgetCode)}>
          <WidgetImage src={imageSrc} alt={imageAlt} />
        </ImageWrapper>
        <WidgetHeader>
          <Typography variant="subtitle1">
            <strong>{title}</strong>
            {subtitle && <> {subtitle}</>}
          </Typography>
          <Button
            color="secondary"
            disableElevation
            variant="contained"
            onClick={() => copy(widgetCode, 'textarea', 'Code')}
          >
            Copy Code
          </Button>
        </WidgetHeader>
      </div>
    </Popover>
  );
};

WidgetCard.propTypes = {
  /** Popover text shown over the card while `draftStatus` is true. */
  draftMessage: PropTypes.string.isRequired,
  /** Marks the widget's entity as a draft: warning border plus the `draftMessage` popover. */
  draftStatus: PropTypes.bool.isRequired,
  imageAlt: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  /** Called with `widgetCode` when the preview image is clicked. */
  onImageClick: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  /** HTML embed snippet copied by the button and passed to `onImageClick`. */
  widgetCode: PropTypes.string.isRequired,
};

WidgetCard.defaultProps = {
  subtitle: null,
};

export default WidgetCard;
